import React from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  BarChart3,
  CheckCircle,
  Globe,
  Award,
  Mail,
  Star,
  Target,
  Leaf,
  Users,
  FileCheck,
  Building2,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const EcoVadis_Support = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0f472f] via-[#1a5d47] to-[#1a8f5e] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-400 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-500 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <TrendingUp size={18} className="text-emerald-300" />
            <span>Sustainability Rating Support</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300">
            EcoVadis Support
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Achieve Higher Sustainability Ratings and Improve Your ESG
            Performance
          </p>
        </motion.div>
      </section>

      {/* What is EcoVadis Support */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is EcoVadis Support?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              EcoVadis Support helps businesses achieve a better sustainability
              rating by providing guidance on data collection, documentation,
              reporting, and strategy implementation for improving ESG
              (Environmental, Social, Governance) factors.
            </p>
            <p>
              The support covers all aspects of the EcoVadis sustainability
              evaluation process, from understanding assessment criteria to
              improving performance in environmental impact, labor practices,
              fair business practices, and supply chain sustainability.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of EcoVadis Support
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Comprehensive Sustainability Assessment",
                desc: "Holistic evaluation of your company's ESG performance including environmental, labor, and ethical practices.",
                icon: BarChart3,
              },
              {
                title: "Data Collection Guidance",
                desc: "Assistance in gathering and analyzing data for the EcoVadis self-assessment.",
                icon: FileCheck,
              },
              {
                title: "Score Improvement Strategies",
                desc: "Identify areas of improvement and develop strategies for enhancing your EcoVadis score.",
                icon: TrendingUp,
              },
              {
                title: "Customized Action Plans",
                desc: "Tailored action plans to address gaps and improve sustainability performance.",
                icon: Target,
              },
              {
                title: "Supply Chain Transparency",
                desc: "Support ensuring suppliers meet sustainability standards and improving overall score.",
                icon: Globe,
              },
              {
                title: "Strategic Reporting",
                desc: "Professional documentation and reporting for EcoVadis evaluation.",
                icon: Award,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-emerald-600 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* Sustainability Services */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Comprehensive Sustainability Services
          </h2>
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              EcoVadis Support includes:
            </p>
            <ul className="space-y-3">
              {[
                "EcoVadis Self-Assessment Guidance - Complete data collection support",
                "Strategy Development for Sustainability - Actionable sustainability planning",
                "Score Improvement Support - Addressing weak areas in your practices",
                "Reporting and Documentation - Proper documentation for evaluation",
                "Supply Chain Engagement - Ensuring supplier compliance with standards",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Why EcoVadis Support Matters
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Strengthen Your Sustainability Practices - Improve ESG performance across operations",
              "Enhance Corporate Reputation - Signal commitment to sustainability to stakeholders",
              "Improve Supply Chain Management - Ensure supplier compliance with standards",
              "Access to New Markets and Clients - Meet procurement requirements of major companies",
              "Meet Regulatory Standards - Align with international sustainability requirements",
              "Investor Confidence - Demonstrate commitment to responsible business practices",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-emerald-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Key Benefits of EcoVadis Support
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Improved Sustainability Rating - Higher EcoVadis scores",
              "Competitive Advantage - Stand out through sustainability commitment",
              "Better Supply Chain Engagement - Stronger supplier relationships",
              "Increased Transparency - Demonstrate sustainability practices to stakeholders",
              "Operational Efficiency - Optimize processes and reduce waste",
              "Market Access - Meet requirements of major buyers and retailers",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-emerald-100 to-teal-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-emerald-700 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of EcoVadis Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Global Recognition in sustainability performance",
              "Enhanced Brand Reputation with stakeholders",
              "Supplier Management and transparency",
              "Risk Mitigation in supply chains",
              "Operational Cost Savings through efficiency",
              "Regulatory Compliance assurance",
              "Market Differentiation and competitive edge",
              "Employee Engagement and satisfaction",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries Using EcoVadis Support
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Manufacturing and Processing",
              "Food and Beverage",
              "Retail and Distribution",
              "Fashion and Textiles",
              "Technology and Electronics",
              "Supply Chain and Logistics",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg"
              >
                <Building2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Get Support */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Get EcoVadis Support
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Initial Consultation",
                desc: "Discuss your sustainability goals and current ESG performance",
              },
              {
                step: 2,
                title: "Sustainability Assessment",
                desc: "Complete gap analysis against EcoVadis sustainability criteria",
              },
              {
                step: 3,
                title: "Data Collection",
                desc: "Gather necessary sustainability data from your organization",
              },
              {
                step: 4,
                title: "Strategy Development",
                desc: "Develop actionable sustainability improvement plans",
              },
              {
                step: 5,
                title: "Self-Assessment Preparation",
                desc: "Guide through EcoVadis self-assessment completion",
              },
              {
                step: 6,
                title: "Performance Monitoring",
                desc: "Monitor and track sustainability metrics progress",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for EcoVadis Support?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions provides expert EcoVadis support services, guiding
              businesses through the sustainability rating process to enhance
              ESG performance and improve sustainability scores.
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "EcoVadis Self-Assessment Guidance",
                "Sustainability Data Collection",
                "Score Improvement Strategies",
                "Supply Chain Engagement",
                "Strategy Development",
                "Ongoing Performance Monitoring",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help businesses optimize their EcoVadis score, improve
              sustainability practices, and enhance their reputation in global
              markets.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default EcoVadis_Support;
